import { highlighterLogger } from './utils.js';
import { getAllElementsIncludingShadow } from './utils.js';

export function findDropdowns() {
  const dropdowns = [];
  
  // Native select elements
  dropdowns.push(...getAllElementsIncludingShadow('select'));
  
  // Elements with dropdown roles that don't have <input>..</input>
  const roleElements = getAllElementsIncludingShadow('[role="combobox"], [role="listbox"], [role="dropdown"], [role="option"], [role="menu"], [role="menuitem"]').filter(el => {
    return el.tagName.toLowerCase() !== 'input' || !["button", "checkbox", "radio"].includes(el.getAttribute("type"));
  });
  dropdowns.push(...roleElements);
  
  // Common dropdown class patterns
  const dropdownPattern = /.*(dropdown|select|combobox|menu).*/i;
  const elements = getAllElementsIncludingShadow('*');
  const dropdownClasses = Array.from(elements).filter(el => {
    const hasDropdownClass = dropdownPattern.test(el.className);
    const validTag = ['li', 'ul', 'span', 'div', 'p', 'a', 'button'].includes(el.tagName.toLowerCase());
    const style = window.getComputedStyle(el); 
    const result = hasDropdownClass && validTag && (style.cursor === 'pointer' || el.tagName.toLowerCase() === 'a' || el.tagName.toLowerCase() === 'button');
    return result;
  });
  
  dropdowns.push(...dropdownClasses);
  
  // Elements with aria-haspopup attribute
  dropdowns.push(...getAllElementsIncludingShadow('[aria-haspopup="true"], [aria-haspopup="listbox"], [aria-haspopup="menu"]'));

  // Improve navigation element detection
  // Semantic nav elements with list items
  dropdowns.push(...getAllElementsIncludingShadow('nav ul li, nav ol li'));
  
  // Navigation elements in common design patterns
  dropdowns.push(...getAllElementsIncludingShadow('header a, .header a, .nav a, .navigation a, .menu a, .sidebar a, aside a'));
  
  // Elements in primary navigation areas with common attributes
  dropdowns.push(...getAllElementsIncludingShadow('[role="navigation"] a, [aria-label*="navigation"] a, [aria-label*="menu"] a'));

  return dropdowns;
}

export function findClickables() {
  const clickables = [];
  
  const checkboxPattern = /checkbox/i;
  // Collect all clickable elements first
  const nativeLinks = [...getAllElementsIncludingShadow('a')];
  const nativeButtons = [...getAllElementsIncludingShadow('button')];
  const inputButtons = [...getAllElementsIncludingShadow('input[type="button"], input[type="submit"], input[type="reset"]')];
  const roleButtons = [...getAllElementsIncludingShadow('[role="button"]')];
  // const tabbable = [...getAllElementsIncludingShadow('[tabindex="0"]')];
  const clickHandlers = [...getAllElementsIncludingShadow('[onclick]')];
  const dropdowns = findDropdowns();
  const nativeCheckboxes = [...getAllElementsIncludingShadow('input[type="checkbox"]')];  
  const fauxCheckboxes = getAllElementsIncludingShadow('*').filter(el => {
    if (checkboxPattern.test(el.className)) {
      const realCheckboxes = getAllElementsIncludingShadow('input[type="checkbox"]', el)
      if (realCheckboxes.length === 1) {
        const boundingRect = realCheckboxes[0].getBoundingClientRect();
        return boundingRect.width <= 1 && boundingRect.height <= 1      
      }
    }
    return false;
  });
  const nativeRadios = [...getAllElementsIncludingShadow('input[type="radio"]')];
  const toggles = findToggles();
  const pointerElements = findElementsWithPointer();
  // Add all elements at once
  clickables.push(
    ...nativeLinks,
    ...nativeButtons,
    ...inputButtons,
    ...roleButtons,
    // ...tabbable,
    ...clickHandlers,
    ...dropdowns,
    ...nativeCheckboxes,
    ...fauxCheckboxes,
    ...nativeRadios,
    ...toggles,
    ...pointerElements
  );

  // Only uniquify once at the end
  return clickables;  // Let findElements handle the uniquification
}

export function findToggles() {
  const toggles = [];
  const checkboxes = getAllElementsIncludingShadow('input[type="checkbox"]');
  const togglePattern = /switch|toggle|slider/i;

  checkboxes.forEach(checkbox => {
    let isToggle = false;

    // Check the checkbox itself
    if (togglePattern.test(checkbox.className) || togglePattern.test(checkbox.getAttribute('role') || '')) {
      isToggle = true;
    }

    // Check parent elements (up to 3 levels)
    if (!isToggle) {
      let element = checkbox;
      for (let i = 0; i < 3; i++) {
        const parent = element.parentElement;
        if (!parent) break;

        const className = parent.className || '';
        const role = parent.getAttribute('role') || '';

        if (togglePattern.test(className) || togglePattern.test(role)) {
          isToggle = true;
          break;
        }
        element = parent;
      }
    }

    // Check next sibling
    if (!isToggle) {
      const nextSibling = checkbox.nextElementSibling;
      if (nextSibling) {
        const className = nextSibling.className || '';
        const role = nextSibling.getAttribute('role') || '';
        if (togglePattern.test(className) || togglePattern.test(role)) {
          isToggle = true;
        }
      }
    }

    if (isToggle) {
      toggles.push(checkbox);
    }
  });

  return toggles;
}

export function findNonInteractiveElements() {
  // Get all elements in the document
  const all = Array.from(getAllElementsIncludingShadow('*'));
  
  // Filter elements based on Python implementation rules
  return all.filter(element => {
    if (!element.firstElementChild) {
      const tag = element.tagName.toLowerCase();      
      if (!['select', 'button', 'a'].includes(tag)) {
        const validTags = ['p', 'span', 'div', 'input', 'textarea','td','th'].includes(tag) || /^h\d$/.test(tag) || /text/.test(tag);
        const boundingRect = element.getBoundingClientRect();
        return validTags && boundingRect.height > 1 && boundingRect.width > 1;
      }
    }
    return false;
  });
}



export function findElementsWithPointer() {
  const elements = [];
  const allElements = getAllElementsIncludingShadow('*');
  
  allElements.forEach(element => {
    const style = window.getComputedStyle(element);
    const hasPointer = style.cursor === 'pointer';
    
    if (hasPointer) {
      elements.push(element);
    }
  });
  
  highlighterLogger.debug(`Found ${elements.length} elements with pointer cursor`);
  return elements;
}

export function findCheckables() {
  const elements = [];

  elements.push(...getAllElementsIncludingShadow('input[type="checkbox"]'));
  elements.push(...getAllElementsIncludingShadow('input[type="radio"]'));
  const all_elements = getAllElementsIncludingShadow('label');
  const radioClasses = Array.from(all_elements).filter(el => {
    return /.*radio.*/i.test(el.className);    
  });
  elements.push(...radioClasses);
  return elements;
}

export function findFillables() {
  const elements = [];

  const inputs = [...getAllElementsIncludingShadow('input:not([type="radio"]):not([type="checkbox"])')];
  highlighterLogger.debug('Found inputs:', inputs.length, inputs);
  elements.push(...inputs);
  
  const textareas = [...getAllElementsIncludingShadow('textarea')];
  highlighterLogger.debug('Found textareas:', textareas.length);
  elements.push(...textareas);
  
  const editables = [...getAllElementsIncludingShadow('[contenteditable="true"]')];
  highlighterLogger.debug('Found editables:', editables.length);
  elements.push(...editables);

  return elements;
}
